<template>
  <div class="app-sidebar-root">
    <!-- Mobile Topbar (shown only ≤768px, replaces the sidebar) -->
    <div class="mobile-topbar">
      <button class="mobile-menu-btn" @click="isMobileMenuOpen = true" aria-label="เปิดเมนู">
        <i class="bi bi-list"></i>
      </button>
      <img src="@/assets/FIQ_TEXT.png" class="mobile-brand-logo" alt="FrostIQ" />
    </div>

    <!-- Mobile drawer backdrop -->
    <div v-if="isMobileMenuOpen" class="mobile-backdrop" @click="isMobileMenuOpen = false"></div>

    <aside class="sidebar" :class="{ collapsed: effectiveCollapsed, 'mobile-open': isMobileMenuOpen }">
      <!-- Toggle Button (desktop collapse) -->
      <button class="toggle-btn" @click="isCollapsed = !isCollapsed">
        <i :class="isCollapsed ? 'bi bi-chevron-right' : 'bi bi-chevron-left'"></i>
      </button>

      <div class="sidebar-inner">
        <!-- Logo / Brand -->
        <div class="brand">
          <img v-show="!effectiveCollapsed" src="@/assets/FIQ_TEXT.png" class="brand-logo " alt="FrostIQ" />
          <button class="mobile-close-btn" @click="isMobileMenuOpen = false" aria-label="ปิดเมนู">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <!-- Nav Tree -->
        <nav class="nav-tree" style="flex:1">
          <template v-for="group in menu" :key="group.label">
            <!-- Group Header (collapsible) -->
            <div
              class="group-header"
              :class="{ 'group-header--active': isGroupActive(group) }"
              @click="toggleGroup(group)"
            >
              <span class="item-icon"><i :class="group.icon"></i></span>
              <span class="item-label" v-show="!effectiveCollapsed">{{ group.label }}</span>
              <i
                v-if="group.children && !effectiveCollapsed"
                class="bi chevron"
                :class="group.open ? 'bi-chevron-up' : 'bi-chevron-down'"
              ></i>
            </div>

            <!-- Children -->
            <template v-if="group.children && group.open && !effectiveCollapsed">
              <template v-for="child in group.children" :key="child.label">

                <!-- Child with sub-children -->
                <template v-if="child.children">
                  <div
                    class="child-header"
                    :class="{ 'child-header--active': isChildActive(child) }"
                    @click="toggleChild(child)"
                  >
                    <span class="item-icon child-icon"><i :class="child.icon"></i></span>
                    <span class="item-label">{{ child.label }}</span>
                    <i
                      class="bi chevron"
                      :class="child.open ? 'bi-chevron-up' : 'bi-chevron-down'"
                    ></i>
                  </div>
                  <template v-if="child.open">
                    <router-link
                      v-for="sub in child.children"
                      :key="sub.label"
                      :to="sub.to"
                      class="sub-item"
                    >
                      <span class="sub-bullet"><i :class="sub.icon"></i></span>
                      <span class="item-label">{{ sub.label }}</span>
                    </router-link>
                  </template>
                </template>

                <!-- Simple child link -->
                <router-link
                  v-else
                  :to="child.to"
                  class="child-item"
                >
                  <span class="item-icon child-icon"><i :class="child.icon"></i></span>
                  <span class="item-label">{{ child.label }}</span>
                </router-link>

              </template>
            </template>
          </template>
        </nav>

        <!-- Logout -->
        <div class="logout-btn" @click="showLogoutModal = true">
          <span class="item-icon logout-icon"><i class="bi bi-box-arrow-right"></i></span>
          <span class="item-label" v-show="!effectiveCollapsed">ออกจากระบบ</span>
        </div>

        <!-- Current role -->
        <div v-if="roleLabel" class="role-row" v-show="!effectiveCollapsed">
          <div class="role-tag">
            <i class="bi bi-person-badge"></i>
            <span>{{ roleLabel }}</span>
          </div>
          <button class="role-action-btn" title="เปลี่ยนรหัสผ่าน" @click="openChangePassword">
            <i class="bi bi-key-fill"></i>
          </button>
        </div>
      </div>

      <!-- Logout Confirm Modal -->
      <transition name="logout-fade">
        <div v-if="showLogoutModal" class="logout-overlay" @click.self="showLogoutModal = false">
          <div class="logout-modal">
            <div class="logout-modal-icon"><i class="bi bi-box-arrow-right"></i></div>
            <div class="logout-modal-title">ออกจากระบบ?</div>
            <p class="logout-modal-text">คุณจะต้องเข้าสู่ระบบใหม่อีกครั้งเพื่อเข้าใช้งานแดชบอร์ด</p>
            <div class="logout-modal-actions">
              <button class="logout-btn-cancel" @click="showLogoutModal = false">ยกเลิก</button>
              <button class="logout-btn-confirm" @click="logout">
                <i class="bi bi-box-arrow-right"></i> ออกจากระบบ
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Change Password Modal -->
      <transition name="logout-fade">
        <div v-if="showChangePasswordModal" class="logout-overlay" @click.self="closeChangePassword">
          <div class="cp-modal">
            <div class="cp-modal-header">
              <div class="cp-modal-icon"><i class="bi bi-key-fill"></i></div>
              <div class="cp-modal-heading">
                <div class="cp-modal-title">เปลี่ยนรหัสผ่าน</div>
                <p class="cp-modal-sub">อัปเดตรหัสผ่านสำหรับบัญชีของคุณ</p>
              </div>
              <button class="cp-modal-close" @click="closeChangePassword"><i class="bi bi-x-lg"></i></button>
            </div>
            <div class="cp-modal-body">
              <div v-if="cpSuccess" class="cp-success">
                <i class="bi bi-check-circle-fill"></i> เปลี่ยนรหัสผ่านสำเร็จ
              </div>
              <template v-else>
                <div class="cp-field">
                  <label>รหัสผ่านปัจจุบัน <span class="cp-req">*</span></label>
                  <input v-model="cpForm.currentPassword" type="password" placeholder="รหัสผ่านปัจจุบัน" autocomplete="current-password" />
                </div>
                <div class="cp-field">
                  <label>รหัสผ่านใหม่ <span class="cp-req">*</span></label>
                  <input v-model="cpForm.newPassword" type="password" placeholder="รหัสผ่านใหม่" autocomplete="new-password" />
                </div>
                <div class="cp-field">
                  <label>ยืนยันรหัสผ่านใหม่ <span class="cp-req">*</span></label>
                  <input v-model="cpForm.confirmPassword" type="password" placeholder="ยืนยันรหัสผ่านใหม่" autocomplete="new-password" @keyup.enter="submitChangePassword" />
                </div>
                <div v-if="cpError" class="cp-error">
                  <i class="bi bi-exclamation-circle-fill"></i> {{ cpError }}
                </div>
              </template>
            </div>
            <div class="cp-modal-footer">
              <template v-if="!cpSuccess">
                <button class="logout-btn-cancel" @click="closeChangePassword">ยกเลิก</button>
                <button class="cp-btn-confirm" :disabled="cpSaving" @click="submitChangePassword">
                  <span v-if="cpSaving" class="cp-spinner"></span>
                  {{ cpSaving ? 'กำลังบันทึก...' : 'เปลี่ยนรหัสผ่าน' }}
                </button>
              </template>
              <button v-else class="cp-btn-confirm" @click="closeChangePassword">ปิด</button>
            </div>
          </div>
        </div>
      </transition>
    </aside>
  </div>
</template>

<script>
import http from '@/utils/http'
import { can, getRoleLabel } from '@/utils/permissions'

// Menu items may declare a `permission` (string or array of strings, OR'd
// together) naming the capability required to see them. Items without a
// `permission` are always visible. Groups whose children are all filtered
// out are dropped entirely.
function isPermitted(item) {
  if (!item.permission) return true
  const perms = Array.isArray(item.permission) ? item.permission : [item.permission]
  return perms.some(p => can(p))
}

function filterMenu(items) {
  return items.reduce((acc, item) => {
    if (item.children) {
      const children = filterMenu(item.children)
      if (children.length > 0) acc.push({ ...item, children })
      return acc
    }
    if (isPermitted(item)) acc.push(item)
    return acc
  }, [])
}

// Remembers which collapsible groups/children (e.g. "Cold Chain", "Platform
// Management") were left open or closed, keyed by label, across page loads.
const SIDEBAR_OPEN_KEY = 'sidebarOpenGroups'

function loadOpenState() {
  try {
    return JSON.parse(localStorage.getItem(SIDEBAR_OPEN_KEY) || '{}')
  } catch (e) {
    return {}
  }
}

function applyOpenState(items, state) {
  return items.map(item => {
    const next = { ...item }
    if (item.children && Object.prototype.hasOwnProperty.call(state, item.label)) {
      next.open = state[item.label]
    }
    if (next.children) next.children = applyOpenState(next.children, state)
    return next
  })
}

const RAW_MENU = [
  {
    label: 'แดชบอร์ด',
    icon: 'bi bi-house-door',
    to: '/coldchainAll',
    open: false,
    children: null,
    permission: 'viewData',
  },
  {
    label: 'Cold Chain',
    icon: 'bi bi-snow2',
    open: true,
    children: [
      { label: 'ทั้งหมด', icon: 'bi bi-list-ul',    to: '/coldchainAll', permission: 'viewData' },
      { label: 'แผนที่', icon: 'bi bi-geo-alt',    to: '/coldchainMap', permission: 'viewData' },
    ]
  },
  {
    label: 'จัดการระบบ',
    icon: 'bi bi-boxes',
    open: true,
    children: [
      // { label: 'Cars',    icon: 'bi bi-truck',        to: '/settings/cars' },
      { label: 'สถานที่ตั้ง/รถ',   icon: 'bi bi-map',          to: '/settings/plans',   permission: 'managePlan' },
      { label: 'คนขับรถ', icon: 'bi bi-person-badge', to: '/settings/drivers', permission: 'manageDevice' },
    ]
  },
  {
    label: 'ตั้งค่า',
    icon: 'bi bi-gear',
    open: true,
    children: [
      { label: 'บริษัท', icon: 'bi bi-building',     to: '/settings/companies', permission: ['manageAssignedCompany', 'createDeleteCompany'] },
      // { label: 'Users',          icon: 'bi bi-people',       to: '/settings/users' },
      { label: 'ผู้ใช้งาน',  icon: 'bi bi-person-badge', to: '/settings/user-companies', permission: ['createDeleteOperatorViewer', 'createDeleteAdmin', 'createDeleteOperatorDriver'] },
      // Temporarily hidden for every role - re-enable by uncommenting.
      // { label: 'กลุ่มผู้ใช้งาน', icon: 'bi bi-collection',   to: '/settings/groups', permission: 'manageGroup' },
    ]
  },
]

const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed'

export default {
  name: 'AppSidebar',
  data() {
    return {
      // Remembers the collapsed/expanded state across page loads/sessions.
      isCollapsed: localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1',
      isMobile: false,
      isMobileMenuOpen: false,
      showLogoutModal: false,
      showChangePasswordModal: false,
      cpForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
      cpError: '',
      cpSaving: false,
      cpSuccess: false,
      // Filtered once per mount based on the logged-in user's permissions —
      // permissions don't change without a fresh login (full remount).
      // Each group/child's open/closed state is restored from localStorage.
      menu: applyOpenState(filterMenu(RAW_MENU), loadOpenState()),
    }
  },
  computed: {
    // On mobile the collapse feature doesn't apply — the sidebar is always
    // shown fully expanded inside the slide-in drawer.
    effectiveCollapsed() {
      return this.isMobile ? false : this.isCollapsed
    },
    roleLabel() {
      return getRoleLabel()
    },
  },
  watch: {
    '$route'() {
      this.isMobileMenuOpen = false
    },
    isCollapsed(val) {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, val ? '1' : '0')
    },
  },
  mounted() {
    this._mql = window.matchMedia('(max-width: 768px)')
    this.isMobile = this._mql.matches
    this._onMqlChange = e => {
      this.isMobile = e.matches
      if (!this.isMobile) this.isMobileMenuOpen = false
    }
    this._mql.addEventListener('change', this._onMqlChange)
  },
  beforeUnmount() {
    if (this._mql && this._onMqlChange) this._mql.removeEventListener('change', this._onMqlChange)
  },
  methods: {
    toggleGroup(group) {
      if (group.children) {
        group.open = !group.open
        this.saveOpenState()
      } else if (group.to) {
        this.$router.push(group.to)
      }
    },
    toggleChild(child) {
      child.open = !child.open
      this.saveOpenState()
    },
    // Persists which groups/children are currently open, keyed by label.
    saveOpenState() {
      const state = {}
      const walk = items => {
        items.forEach(item => {
          if (item.children) {
            state[item.label] = !!item.open
            walk(item.children)
          }
        })
      }
      walk(this.menu)
      localStorage.setItem(SIDEBAR_OPEN_KEY, JSON.stringify(state))
    },
    isGroupActive(group) {
      if (!group.children) return this.$route.path === group.to
      return group.children.some(c =>
        c.children
          ? c.children.some(s => this.$route.path === s.to)
          : this.$route.path === c.to
      )
    },
    isChildActive(child) {
      return child.children
        ? child.children.some(s => this.$route.path === s.to)
        : this.$route.path === child.to
    },
    logout() {
      this.showLogoutModal = false
      localStorage.clear()
      this.$router.push('/login')
    },
    openChangePassword() {
      this.cpForm = { currentPassword: '', newPassword: '', confirmPassword: '' }
      this.cpError = ''
      this.cpSuccess = false
      this.cpSaving = false
      this.showChangePasswordModal = true
    },
    closeChangePassword() {
      if (this.cpSaving) return
      this.showChangePasswordModal = false
    },
    async submitChangePassword() {
      this.cpError = ''
      const { currentPassword, newPassword, confirmPassword } = this.cpForm

      if (!currentPassword || !newPassword || !confirmPassword) {
        this.cpError = 'กรุณากรอกข้อมูลให้ครบทุกช่อง'
        return
      }
      if (newPassword.length < 6) {
        this.cpError = 'รหัสผ่านใหม่ต้องมีอย่างน้อย 6 ตัวอักษร'
        return
      }
      if (newPassword !== confirmPassword) {
        this.cpError = 'รหัสผ่านใหม่และการยืนยันไม่ตรงกัน'
        return
      }
      if (newPassword === currentPassword) {
        this.cpError = 'รหัสผ่านใหม่ต้องไม่ซ้ำกับรหัสผ่านปัจจุบัน'
        return
      }

      this.cpSaving = true
      try {
        await http.post(
          '/auth/change-password',
          { currentPassword, newPassword },
          { headers: { authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
        )
        this.cpSuccess = true
      } catch (err) {
        const status = err.response?.status
        this.cpError =
          status === 401 ? 'รหัสผ่านปัจจุบันไม่ถูกต้อง' :
          status === 400 ? 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง' :
          'เปลี่ยนรหัสผ่านไม่สำเร็จ กรุณาลองใหม่อีกครั้งในภายหลัง'
      } finally {
        this.cpSaving = false
      }
    },
  }
}
</script>

<style scoped>
/* ── Layout ── */
.sidebar {
  position: relative;
  width: 240px;
  min-height: 100vh;
  background: #fff;
  border-right: 1px solid #efeffa;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  transition: width 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 2px 0 16px rgba(80, 60, 220, 0.06);
}
.sidebar.collapsed { width: 64px; }

/* ── Toggle ── */
.toggle-btn {
  position: absolute;
  top: 22px;
  right: -13px;
  z-index: 20;
  width: 26px; height: 26px;
  border-radius: 50%;
  background: #57A9F4;
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px;
  box-shadow: 0 2px 8px rgba(91,79,255,0.4);
  transition: background 0.2s;
}
.toggle-btn:hover { background: #3a91d9; }

/* ── Inner ── */
.sidebar-inner {
  padding: 20px 12px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow: hidden;
  height: 100%;
  min-height: 100vh;
}

/* ── Brand ── */
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 4px 4px;
}
.brand-logo {
  height: 24px;
  width: auto;
  object-fit: contain;
}
.mobile-close-btn {
  display: none;
  margin-left: auto;
  width: 30px; height: 30px; border-radius: 8px; border: none;
  background: #f3f4f6; color: #6b7280; cursor: pointer; font-size: 14px;
  align-items: center; justify-content: center; flex-shrink: 0;
}

/* ── Mobile Topbar (hidden on desktop) ── */
.mobile-topbar {
  display: none;
  align-items: center;
  gap: 12px;
  height: 56px;
  padding: 0 16px;
  background: #fff;
  border-bottom: 1px solid #efeffa;
  position: sticky;
  top: 0;
  z-index: 900;
  box-shadow: 0 1px 8px rgba(0,0,0,0.05);
}
.mobile-menu-btn {
  width: 36px; height: 36px; border-radius: 9px; border: none;
  background: #f0f6ff; color: #3a7bd5; font-size: 18px;
  display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0;
}
.mobile-brand-logo { height: 22px; width: auto; object-fit: contain; }
.mobile-backdrop { display: none; }

/* ── Nav Tree ── */
.nav-tree {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* ── Shared item base ── */
.item-icon {
  font-size: 15px;
  flex-shrink: 0;
  width: 20px;
  display: flex; align-items: center; justify-content: center;
  color: #888;
}
.item-label {
  flex: 1;
  font-size: 13.5px;
  white-space: nowrap;
  overflow: hidden;
}
.chevron {
  font-size: 11px;
  color: #bbb;
  flex-shrink: 0;
  transition: transform 0.2s;
}

/* ── Group Header (top-level) ── */
.group-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 10px;
  cursor: pointer;
  color: #555;
  font-weight: 600;
  font-size: 13.5px;
  transition: background 0.15s, color 0.15s;
  user-select: none;
}
.group-header:hover {
  background: #eef6fe;
  color: #57A9F4;
}
.group-header:hover .item-icon { color: #57A9F4; }
.group-header--active {
  color: #57A9F4;
}
.group-header--active .item-icon { color: #57A9F4; }

/* ── Child Header (2nd level, collapsible) ── */
.child-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px 7px 32px;
  border-radius: 10px;
  cursor: pointer;
  color: #666;
  font-weight: 500;
  font-size: 13px;
  transition: background 0.15s, color 0.15s;
  user-select: none;
}
.child-header:hover { background: #eef6fe; color: #57A9F4; }
.child-header--active { color: #57A9F4; font-weight: 600; }
.child-header--active .item-icon { color: #57A9F4; }

.child-icon { color: #999; font-size: 14px; }

/* ── Child Item (2nd level, link) ── */
.child-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px 7px 32px;
  border-radius: 10px;
  text-decoration: none;
  color: #666;
  font-size: 13px;
  font-weight: 500;
  transition: background 0.15s, color 0.15s;
}
.child-item:hover { background: #eef6fe; color: #57A9F4; }
.child-item.router-link-active {
  background: #e8f4fe;
  color: #57A9F4;
  font-weight: 600;
}
.child-item.router-link-active .item-icon { color: #57A9F4; }

/* ── Sub Item (3rd level) ── */
.sub-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px 6px 52px;
  border-radius: 10px;
  text-decoration: none;
  color: #888;
  font-size: 13px;
  font-weight: 400;
  transition: background 0.15s, color 0.15s;
}
.sub-item:hover { background: #eef6fe; color: #57A9F4; }
.sub-item.router-link-active {
  background: #e8f4fe;
  color: #57A9F4;
  font-weight: 600;
}
.sub-bullet {
  font-size: 13px;
  color: #bbb;
  flex-shrink: 0;
}
.sub-item.router-link-active .sub-bullet { color: #57A9F4; }

/* ── Logout ── */
.logout-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 10px;
  cursor: pointer;
  color: #e05252;
  font-weight: 600;
  font-size: 13.5px;
  transition: background 0.15s, color 0.15s;
  user-select: none;
  margin-top: auto;
}
.logout-btn:hover {
  background: #fef2f2;
  color: #c0392b;
}
.logout-icon {
  color: #e05252 !important;
}
.logout-btn:hover .logout-icon {
  color: #c0392b !important;
}

/* ── Role tag ── */
.role-tag {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 10px;
  background: #f0f6ff;
  color: #3a7bd5;
  font-weight: 600;
  font-size: 12px;
  text-transform: capitalize;
  letter-spacing: 0.2px;
}
.role-tag i { font-size: 13px; flex-shrink: 0; }

.role-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.role-row .role-tag { flex: 1; min-width: 0; }
.role-action-btn {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1.5px solid #e4edf8;
  background: #fff;
  color: #5a6a82;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.role-action-btn:hover {
  background: #f0f6ff;
  border-color: #bcd6f9;
  color: #3a7bd5;
}

/* ── Change Password Modal ── */
.cp-modal {
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 380px;
  padding: 24px 24px 22px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.18);
}
.cp-modal-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 18px;
}
.cp-modal-icon {
  width: 40px; height: 40px;
  flex-shrink: 0;
  border-radius: 12px;
  background: #f0f6ff;
  color: #3a7bd5;
  display: flex; align-items: center; justify-content: center;
  font-size: 17px;
}
.cp-modal-heading { flex: 1; min-width: 0; }
.cp-modal-title {
  font-size: 15.5px;
  font-weight: 700;
  color: #1a2233;
}
.cp-modal-sub {
  font-size: 12px;
  color: #8a96a8;
  margin: 3px 0 0;
  line-height: 1.4;
}
.cp-modal-close {
  flex-shrink: 0;
  width: 28px; height: 28px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #9aa5b5;
  font-size: 13px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s, color 0.15s;
}
.cp-modal-close:hover { background: #f7f9fc; color: #5a6a82; }

.cp-modal-body { display: flex; flex-direction: column; gap: 14px; }
.cp-field { display: flex; flex-direction: column; gap: 6px; }
.cp-field label {
  font-size: 12.5px;
  font-weight: 600;
  color: #3a4557;
}
.cp-req { color: #e05252; }
.cp-field input {
  height: 40px;
  border-radius: 10px;
  border: 1.5px solid #e4edf8;
  padding: 0 12px;
  font-size: 13px;
  color: #1a2233;
  outline: none;
  transition: border-color 0.15s;
  font-family: inherit;
}
.cp-field input:focus { border-color: #57A9F4; }

.cp-error {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 12px;
  border-radius: 10px;
  background: #fef2f2;
  color: #e05252;
  font-size: 12.5px;
  line-height: 1.4;
}
.cp-success {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 12px;
  border-radius: 10px;
  background: #f0faf4;
  color: #2e9e5b;
  font-size: 13.5px;
  font-weight: 600;
}

.cp-modal-footer {
  display: flex;
  gap: 8px;
  margin-top: 20px;
}
.cp-btn-confirm {
  flex: 1;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: #57A9F4;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  transition: background 0.15s;
}
.cp-btn-confirm:hover:not(:disabled) { background: #3a90e0; }
.cp-btn-confirm:disabled { opacity: 0.7; cursor: not-allowed; }

.cp-spinner {
  width: 14px; height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  animation: cp-spin 0.7s linear infinite;
}
@keyframes cp-spin { to { transform: rotate(360deg); } }

/* ── Logout Confirm Modal ── */
.logout-overlay {
  position: fixed;
  inset: 0;
  background: rgba(20, 25, 40, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 20px;
}
.logout-modal {
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 340px;
  padding: 26px 24px 22px;
  text-align: center;
  box-shadow: 0 24px 60px rgba(0,0,0,0.18);
}
.logout-modal-icon {
  width: 48px; height: 48px;
  margin: 0 auto 14px;
  border-radius: 14px;
  background: #fef2f2;
  color: #e05252;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px;
}
.logout-modal-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a2233;
  margin-bottom: 6px;
}
.logout-modal-text {
  font-size: 12.5px;
  color: #8a96a8;
  margin: 0 0 20px;
  line-height: 1.5;
}
.logout-modal-actions {
  display: flex;
  gap: 8px;
}
.logout-btn-cancel {
  flex: 1;
  height: 40px;
  border-radius: 10px;
  border: 1.5px solid #e4edf8;
  background: #fff;
  color: #5a6a82;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.logout-btn-cancel:hover { background: #f7f9fc; border-color: #d8e0ec; }
.logout-btn-confirm {
  flex: 1;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: #e05252;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  transition: background 0.15s;
}
.logout-btn-confirm:hover { background: #c0392b; }

.logout-fade-enter-active, .logout-fade-leave-active { transition: opacity 0.15s; }
.logout-fade-enter-from, .logout-fade-leave-to { opacity: 0; }

/* ── Mobile / Tablet: sidebar becomes a topbar + slide-in drawer ── */
@media (max-width: 768px) {
  .mobile-topbar { display: flex; }
  .mobile-close-btn { display: flex; }
  .toggle-btn { display: none; }

  .sidebar {
    position: fixed;
    top: 0; left: 0;
    height: 100vh;
    width: 260px;
    max-width: 84vw;
    /* Above any page's own floating UI (map overlays, filter panels, etc.
       commonly sit at z-index 1000+) so the nav drawer always stays on top
       of the page content behind it, not underneath it. */
    z-index: 5000;
    transform: translateX(-100%);
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 4px 0 24px rgba(0,0,0,0.18);
  }
  .sidebar.collapsed { width: 260px; }
  .sidebar.mobile-open { transform: translateX(0); }

  .sidebar-inner { min-height: 100vh; }

  .mobile-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(20, 25, 40, 0.4);
    z-index: 4999;
  }
}
</style>
